import React, { useEffect, useState } from "react";
import { useLang } from "@/context/LangContext";

// MELLETIC premium splash — exactly 4.0s.
// Signature motion: PRECISION FRAGMENTS -> RAPID ASSEMBLY -> PERFECT LOCK -> STILLNESS -> APP.
// The whole identity (M + liquid-glass orb + MELLETIC + "Speak it. Live it.") assembles
// from elegant fragments that fly into their exact positions almost simultaneously.
// After the lock (~1.35s) NOTHING moves until the seamless dissolve into Home.
// Timeline: 0.00–0.20 fragments · 0.25–2.00 assembly (slower, visible) · 2.00–2.20 lock (pulse + micro settle) ·
// 2.20–3.55 perfect stillness · 3.55–4.00 crossfade.
export default function Splash({ onDone }) {
  const [exiting, setExiting] = useState(false);
  const { t } = useLang();

  useEffect(() => {
    const t1 = setTimeout(() => setExiting(true), 3550);
    const t2 = setTimeout(() => onDone && onDone(), 4000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [onDone]);

  return (
    <div
      className={`fixed inset-0 z-[100] splash-bg overflow-hidden pointer-events-none ${exiting ? "splash-exit" : ""}`}
      style={{ paddingTop: "env(safe-area-inset-top,0px)", paddingBottom: "env(safe-area-inset-bottom,0px)" }}
      aria-hidden="true"
      data-testid="splash-screen"
    >
      {/* premium light field: soft aura + concentric rings + floor reflection */}
      <div className="splash-glow absolute inset-0 pointer-events-none overflow-hidden">
        {/* soft violet/blue aura */}
        <span style={{
          position: "absolute", left: "50%", top: "48%", transform: "translate(-50%,-50%)",
          width: "min(122vw,640px)", height: "min(122vw,640px)", borderRadius: "50%",
          background: "radial-gradient(circle, rgba(37,99,235,0.11) 0%, rgba(37,99,235,0.07) 32%, rgba(34,211,238,0.035) 52%, transparent 68%)",
          filter: "blur(44px)",
        }} />
        {/* floor reflection / light pool */}
        <span style={{
          position: "absolute", left: "50%", top: "72%", transform: "translateX(-50%)",
          width: "min(94vw,460px)", height: "150px",
          background: "radial-gradient(ellipse at center, rgba(165,175,255,0.24) 0%, rgba(37,99,235,0.07) 40%, transparent 72%)",
          filter: "blur(20px)",
        }} />
      </div>

      {/* one unified brand composition, perfectly centered */}
      <div className="absolute inset-0 flex flex-col items-center justify-center px-6">
        <div className="splash-assembly flex flex-col items-center">
          {/* ONE unified MELLETIC M — the ribbon flows into its spherical endpoint (single asset, single material) */}
          <div className="relative" style={{ width: "min(72vw,300px)", aspectRatio: "490 / 243" }}>
            <div className="frag frag-m w-full h-full select-none">
              <div style={{
                width: "100%", height: "100%",
                WebkitMaskImage: "url(/melletic-mark.png)", maskImage: "url(/melletic-mark.png)",
                WebkitMaskSize: "contain", maskSize: "contain",
                WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
                WebkitMaskPosition: "center", maskPosition: "center",
                background: "linear-gradient(135deg, #bcddff 0%, #6aa6f7 30%, #2f6fe0 62%, #17368f 100%)",
                filter: "drop-shadow(0 6px 11px rgba(23,42,110,0.45)) drop-shadow(0 -1px 0 rgba(255,255,255,0.5))",
              }} />
            </div>

            {/* single light sweep across the whole symbol at lock */}
            <div className="pointer-events-none absolute inset-0 overflow-hidden" style={{
              WebkitMaskImage: "url(/melletic-mark.png)", maskImage: "url(/melletic-mark.png)",
              WebkitMaskSize: "contain", maskSize: "contain",
              WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
              WebkitMaskPosition: "center", maskPosition: "center",
            }}>
              <span className="absolute inset-y-0 -left-1/3 w-1/2 splash-sheen"
                style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,.8), transparent)" }} />
            </div>

            {/* optical light pulse through the completed symbol at lock */}
            <span className="splash-lockpulse" style={{
              position: "absolute", left: "50%", top: "50%", width: "78%", height: "150%",
              transform: "translate(-50%,-50%)", borderRadius: "50%",
              background: "radial-gradient(circle, rgba(37,99,235,0.12) 0%, rgba(37,99,235,0.06) 44%, transparent 66%)",
              filter: "blur(8px)", pointerEvents: "none",
            }} />
          </div>

          {/* wordmark */}
          <h1 className="frag frag-word mt-4 font-heading"
            style={{ color: "#0B1727", fontWeight: 600, letterSpacing: "0.44em", fontSize: "clamp(22px,6.6vw,32px)", paddingLeft: "0.44em" }}>
            MELLETIC
          </h1>

          {/* engraved tagline */}
          <p className="frag frag-tag mt-2.5"
            style={{
              fontSize: "clamp(14px,4.2vw,18px)", fontWeight: 600, letterSpacing: "0.01em",
              color: "#aeb8c8",
              textShadow: "0 1px 0 rgba(255,255,255,0.95), 0 -1px 1px rgba(11,23,39,0.20)",
            }}>
            {t("tagline_a")} {t("tagline_b")}
          </p>
        </div>
      </div>
    </div>
  );
}
